import { useState } from 'react';
import './TouchpointTimeline.css';

const TYPE_ICONS = { CALL: '📞', WHATSAPP: '💬', IN_PERSON: '🤝', VIDEO_CALL: '📹' };
const SENTIMENT_META = {
  WARMING: { label: 'Warming', cls: 'ltt__sentiment--warming' },
  NEUTRAL: { label: 'Neutral', cls: 'ltt__sentiment--neutral' },
  COOLING: { label: 'Cooling', cls: 'ltt__sentiment--cooling' },
};

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function ChipList({ items, cls, prefix }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="ltt__chip-list">
      {items.map((item, i) => (
        <span key={i} className={`ltt__chip ${cls}`}>{prefix}{item}</span>
      ))}
    </div>
  );
}

function AiInsights({ tp, onProcess, processing }) {
  const [expanded, setExpanded] = useState(false);

  if (!tp.raw_conversation_text && !tp.ai_processed_at) return null;

  return (
    <div className="ltt__ai-section">
      {tp.raw_conversation_text && !tp.ai_processed_at && (
        <button
          className="ltt__process-btn"
          onClick={() => onProcess(tp.id)}
          disabled={processing}
        >
          {processing ? '⏳ Processing...' : '🤖 Process with AI'}
        </button>
      )}

      {tp.ai_processed_at && (
        <>
          <button
            className="ltt__ai-expand-btn"
            onClick={() => setExpanded(prev => !prev)}
          >
            {expanded ? '▾' : '▸'} AI Insights
            {tp.ai_sentiment && (
              <span className={`ltt__sentiment ${SENTIMENT_META[tp.ai_sentiment]?.cls || ''}`}>
                {SENTIMENT_META[tp.ai_sentiment]?.label || tp.ai_sentiment}
              </span>
            )}
            <span className="ltt__ai-timestamp">processed {formatDateTime(tp.ai_processed_at)}</span>
          </button>

          {expanded && (
            <div className="ltt__ai-content">
              {tp.ai_summary && <p className="ltt__ai-summary">{tp.ai_summary}</p>}
              {tp.ai_interests?.length > 0 && (
                <div className="ltt__ai-row">
                  <span className="ltt__ai-row-label">Interests</span>
                  <ChipList items={tp.ai_interests} cls="ltt__chip--interest" />
                </div>
              )}
              {tp.ai_objections?.length > 0 && (
                <div className="ltt__ai-row">
                  <span className="ltt__ai-row-label">Objections</span>
                  <ChipList items={tp.ai_objections} cls="ltt__chip--objection" />
                </div>
              )}
              {tp.ai_expectations_set?.length > 0 && (
                <div className="ltt__ai-row">
                  <span className="ltt__ai-row-label">Expectations set</span>
                  <ChipList items={tp.ai_expectations_set} cls="ltt__chip--expectation" />
                </div>
              )}
              {tp.ai_flags?.length > 0 && (
                <div className="ltt__ai-row">
                  <span className="ltt__ai-row-label">⚠️ Flags</span>
                  <ChipList items={tp.ai_flags} cls="ltt__chip--flag" prefix="⚠️ " />
                </div>
              )}
              {tp.ai_next_action && (
                <div className="ltt__next-action">
                  <strong>Suggested next action:</strong> {tp.ai_next_action}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function TouchpointTimeline({ touchpoints, onLogTouchpoint, onProcessAi, processingId }) {
  const sorted = [...(touchpoints || [])].reverse();

  return (
    <div className="ltt__timeline">
      <div className="ltt__timeline-header">
        <span className="ltt__timeline-title">Touchpoint History</span>
        <button className="btn--primary ltt__log-btn" onClick={onLogTouchpoint}>
          + Log Touchpoint
        </button>
      </div>

      {sorted.length === 0 && (
        <div className="ltt__empty">No touchpoints yet. Log the first interaction!</div>
      )}

      {sorted.map(tp => (
        <div key={tp.id} className="ltt__entry">
          <div className="ltt__entry-icon">{TYPE_ICONS[tp.type] || '📌'}</div>
          <div className="ltt__entry-body">
            <div className="ltt__entry-top">
              <span className="ltt__entry-type">{tp.type?.replace('_', ' ')}</span>
              <span className={`ltt__entry-outcome ${tp.outcome === 'CONNECTED' || tp.outcome === 'ATTENDED' || tp.outcome === 'REPLIED' ? 'ltt__entry-outcome--positive' : ''}`}>
                {tp.outcome?.replace('_', ' ')}
              </span>
              <span className="ltt__entry-time">{formatDateTime(tp.touched_at || tp.created_at)}</span>
            </div>
            {tp.notes && <p className="ltt__entry-notes">{tp.notes}</p>}
            {tp.duration_minutes && (
              <span className="ltt__entry-duration">{tp.duration_minutes} min</span>
            )}
            <AiInsights
              tp={tp}
              onProcess={onProcessAi}
              processing={processingId === tp.id}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
